import React, { useEffect, useState, lazy, Suspense } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Computer, Palette, PenTool, FileText, GraduationCap, Search, Filter, ArrowLeft, Camera, Music, Languages, HeartPulse, Shirt, ShoppingBag, Home, MessageCircle, Eye, Gamepad2, TrendingUp, Cog, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useServices } from "@/hooks/useServices";
import { useUserProfiles } from "@/hooks/useUserProfiles";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ServiceLocationLabel } from "@/components/ServiceLocationLabel";
const ServiceRegionOverlay = lazy(() => import("@/components/ServiceRegionOverlay").then(m => ({ default: m.ServiceRegionOverlay })));
import { getCachedLocation } from "@/utils/locationUtils";
import { UserAvatar } from "@/components/UserAvatar";
import { AvailabilityDisplay, WeeklySchedule } from "@/components/services/AvailabilitySchedule";

// Enhanced service type that includes pricing_type and delivery_method
interface ServiceWithCategory {
  id: string;
  title: string;
  description: string | null;
  price: number;
  pricing_type?: "fixed" | "hourly";
  category_id: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
  delivery_method?: "virtual" | "in-person" | "both" | null;
  service_location?: {
    latitude: number;
    longitude: number;
    radius: number;
  } | null;
  availability_schedule?: any;
  image_url?: string | null;
  service_categories?: {
    id: string;
    name: string;
    description: string | null;
  };
}
interface ServiceConversation {
  serviceId: string;
  conversationId: string;
}

// Updated category icons map with new categories
const categoryIcons: Record<string, React.FC<{
  className?: string;
}>> = {
  "Coding": Computer,
  "Design": PenTool,
  "Writing": FileText,
  "Marketing and Sales": TrendingUp,
  "Gaming": Gamepad2,
  "Tutoring": GraduationCap,
  "Photography": Camera,
  "Music": Music,
  "Language": Languages,
  "Chores": HeartPulse,
  "Fashion": Shirt,
  "Business": ShoppingBag,
  "Arts": Palette,
  "Engineering and CAD": Cog,
  "Other": Computer
};

// Updated category colors with new categories - now for circular backgrounds
const categoryColors: Record<string, string> = {
  "Coding": "bg-blue-500 text-white",
  "Design": "bg-pink-500 text-white",
  "Writing": "bg-green-500 text-white",
  "Marketing and Sales": "bg-orange-500 text-white",
  "Gaming": "bg-purple-500 text-white",
  "Tutoring": "bg-amber-500 text-white",
  "Photography": "bg-indigo-500 text-white",
  "Music": "bg-red-500 text-white",
  "Language": "bg-emerald-500 text-white",
  "Chores": "bg-rose-500 text-white",
  "Fashion": "bg-fuchsia-500 text-white",
  "Business": "bg-cyan-500 text-white",
  "Arts": "bg-violet-500 text-white",
  "Engineering and CAD": "bg-slate-500 text-white",
  "Other": "bg-gray-500 text-white"
};

// Reorder categories for Fiverr/Upwork appropriateness
const categoryOrder = ["Coding", "Design", "Writing", "Marketing and Sales", "Gaming", "Tutoring", "Photography", "Music", "Language", "Chores", "Fashion", "Business", "Arts", "Engineering and CAD", "Other"];

// Calculate cards to show for exactly 2 rows based on current screen width
const calculateCardsToShow = () => {
  const width = window.innerWidth;
  let cardsPerRow;
  if (width >= 1536) cardsPerRow = 8;      // 2xl
  else if (width >= 1280) cardsPerRow = 7; // xl
  else if (width >= 1024) cardsPerRow = 6; // lg
  else if (width >= 768) cardsPerRow = 5;  // md
  else if (width >= 480) cardsPerRow = 4;  // sm
  else cardsPerRow = 3;                    // xs

  return cardsPerRow * 2; // Always show exactly 2 rows
};
const DiscoverServices = () => {
  const navigate = useNavigate();
  const {
    categoryId
  } = useParams<{
    categoryId?: string;
  }>();
  const {
    categories,
    services,
    isLoading: servicesLoading,
    isCategoriesLoading
  } = useServices();
  const {
    getUserProfile
  } = useUserProfiles();
  const {
    user
  } = useAuth(); // Note: user can be null for unauthenticated users
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(categoryId || "");
  const [priceRange, setPriceRange] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [filteredServices, setFilteredServices] = useState<ServiceWithCategory[]>([]);
  const [userProfiles, setUserProfiles] = useState<Record<string, any>>({});
  const [serviceConversations, setServiceConversations] = useState<ServiceConversation[]>([]);
  const [requestingService, setRequestingService] = useState<string | null>(null);
  const [serviceLocations, setServiceLocations] = useState<Record<string, string>>({});
  const [regionOverlay, setRegionOverlay] = useState<{
    isOpen: boolean;
    serviceLocation?: {
      latitude: number;
      longitude: number;
      radius: number;
    };
    serviceTitle?: string;
  }>({
    isOpen: false
  });
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [cardsToShow, setCardsToShow] = useState(calculateCardsToShow());
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Set up resize listener to update cards to show when window size changes
  useEffect(() => {
    const handleResize = () => {
      const newCardsToShow = calculateCardsToShow();
      console.log(`Window resized, updating cards to show: ${newCardsToShow}`);
      setCardsToShow(newCardsToShow);
    };
    window.addEventListener('resize', handleResize);

    // Initial calculation
    const initialCardsToShow = calculateCardsToShow();
    console.log(`Initial cards to show: ${initialCardsToShow}`);
    setCardsToShow(initialCardsToShow);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Set the selected category if it comes from URL params
  useEffect(() => {
    if (categoryId) {
      setSelectedCategory(categoryId);
    }
  }, [categoryId]);

  // Load user profiles for service creators - works for both authenticated and unauthenticated users
  useEffect(() => {
    const loadUserProfiles = async () => {
      if (services && services.length > 0) {
        const userIds = [...new Set(services.map(service => service.user_id))];
        const profiles: Record<string, any> = {};
        for (const userId of userIds) {
          try {
            const profile = await getUserProfile(userId);
            profiles[userId] = profile;
          } catch (error) {
            console.error(`Error loading profile for ${userId}:`, error);
          }
        }
        setUserProfiles(profiles);
      }
    };
    loadUserProfiles();
  }, [services, getUserProfile]);

  // Load existing conversations related to services - only if user is logged in
  useEffect(() => {
    const loadServiceConversations = async () => {
      if (!user) return; // Skip for unauthenticated users

      try {
        const {
          data,
          error
        } = await supabase.from('conversations').select('id, service_id').not('service_id', 'is', null).eq('service_completed', false).or(`participant1_id.eq.${user.id},participant2_id.eq.${user.id}`);
        if (error) {
          console.error('Error loading service conversations:', error);
          return;
        }
        if (data) {
          const conversations: ServiceConversation[] = data.map(item => ({
            serviceId: item.service_id as string,
            conversationId: item.id as string
          })).filter(item => item.serviceId && item.conversationId);
          setServiceConversations(conversations);
        }
      } catch (error) {
        console.error('Error fetching service conversations:', error);
      }
    };
    loadServiceConversations();
  }, [user]);

  // Load location names for services
  useEffect(() => {
    const loadServiceLocations = async () => {
      if (!services) return;
      const locations: Record<string, string> = {};
      for (const service of services) {
        if (service.service_location && service.delivery_method !== "virtual") {
          try {
            const locationName = await getCachedLocation(service.service_location.latitude, service.service_location.longitude);
            locations[service.id] = locationName;
          } catch (error) {
            console.error(`Error loading location for service ${service.id}:`, error);
          }
        }
      }
      setServiceLocations(locations);
    };
    loadServiceLocations();
  }, [services]);

  // Filter services based on search and selected filters
  useEffect(() => {
    if (!services) return;
    let filtered = [...services] as ServiceWithCategory[];

    // Filter by search query (title or description)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(service => service.title.toLowerCase().includes(query) || service.description && service.description.toLowerCase().includes(query));
    }

    // Filter by category
    if (selectedCategory && selectedCategory !== "all") {
      filtered = filtered.filter(service => service.category_id === selectedCategory);
    }

    // Filter by price range
    if (priceRange && priceRange !== "any") {
      if (priceRange === "100+") {
        filtered = filtered.filter(service => service.price >= 100);
      } else {
        const [min, max] = priceRange.split("-").map(Number);
        filtered = filtered.filter(service => service.price >= min && service.price <= max);
      }
    }

    // Filter by delivery method
    if (deliveryMethod && deliveryMethod !== "any") {
      filtered = filtered.filter(service => {
        if (deliveryMethod === "both") return true;
        return service.delivery_method === deliveryMethod || service.delivery_method === "both";
      });
    }
    setFilteredServices(filtered);
  }, [services, searchQuery, selectedCategory, priceRange, deliveryMethod]);

  // Check if a conversation already exists for a service
  const getConversationForService = (serviceId: string): string | null => {
    const conversation = serviceConversations.find(sc => sc.serviceId === serviceId);
    return conversation ? conversation.conversationId : null;
  };

  // Handle requesting a service - this creates a new conversation
  const handleRequestService = async (service: ServiceWithCategory) => {
    if (!user) {
      toast.info("Please sign in to request this service");
      navigate("/auth?mode=login&redirect=" + encodeURIComponent(window.location.pathname));
      return;
    }
    if (service.user_id === user.id) {
      toast.error("You cannot request your own service");
      return;
    }
    setRequestingService(service.id);
    try {
      // Check if conversation already exists
      const existingConvo = getConversationForService(service.id);
      if (existingConvo) {
        navigate(`/chat/${existingConvo}`);
        return;
      }

      // Create initial message text with service title
      const initialMessage = `Hi, I'm interested in your ${service.title} service.`;

      // Create a new conversation with service_id
      const {
        data: newConvo,
        error: convoError
      } = await supabase.from('conversations').insert({
        participant1_id: user.id,
        participant2_id: service.user_id,
        service_id: service.id,
        last_message: initialMessage
      }).select('id').single();
      if (convoError || !newConvo) {
        throw new Error("Failed to create conversation");
      }

      // Send the initial message
      const {
        error: msgError
      } = await supabase.from('messages').insert({
        conversation_id: newConvo.id,
        sender_id: user.id,
        text: initialMessage
      });
      if (msgError) {
        throw new Error("Failed to send initial message");
      }

      // Add to local state so UI updates
      setServiceConversations(prev => [...prev, {
        serviceId: service.id,
        conversationId: newConvo.id
      }]);
      toast.success("Service requested! Opening conversation...");
      navigate(`/chat/${newConvo.id}`);
    } catch (error) {
      console.error("Error requesting service:", error);
      toast.error("Failed to request service. Please try again.");
    } finally {
      setRequestingService(null);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setPriceRange("");
    setDeliveryMethod("");
  };

  // Get category name by ID
  const getCategoryNameById = (id: string | null) => {
    if (!id) return "Uncategorized";
    const category = categories?.find(cat => cat.id === id);
    return category ? category.name : "Uncategorized";
  };

  // Handle viewing service region
  const handleViewServiceRegion = (service: ServiceWithCategory) => {
    if (!service.service_location) return;
    setRegionOverlay({
      isOpen: true,
      serviceLocation: service.service_location,
      serviceTitle: service.title
    });
  };

  // Handle closing region overlay
  const handleCloseRegionOverlay = () => {
    setRegionOverlay({
      isOpen: false
    });
  };

  // Sort categories by the defined order
  const sortedCategories = categories?.sort((a, b) => {
    const aIndex = categoryOrder.indexOf(a.name);
    const bIndex = categoryOrder.indexOf(b.name);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

  // Show calculated number of cards for two rows unless showAll is true
  const categoriesToShow = showAllCategories ? sortedCategories : sortedCategories?.slice(0, cardsToShow);
  console.log(`Categories available: ${sortedCategories?.length}, Cards to show: ${cardsToShow}, Showing: ${categoriesToShow?.length}`);
  return <div className="min-h-screen bg-background flex flex-col">
      <div className="container mx-auto px-4 py-4 flex-grow">
        <div className="flex items-center mb-3">
          <Link to="/" className="flex items-center text-muted-foreground hover:text-anyskill-purple mr-4">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </Link>
          <h1 className="font-poppins font-bold text-xl md:text-2xl">
            Discover <span className="text-anyskill-blue">Services</span>
          </h1>
        </div>

        {/* Compact Search and Filters */}
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-8" />
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[120px] h-8">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {sortedCategories?.map(category => <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={priceRange} onValueChange={setPriceRange}>
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue placeholder="Price" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Price</SelectItem>
                  <SelectItem value="0-25">$0 - $25</SelectItem>
                  <SelectItem value="25-50">$25 - $50</SelectItem>
                  <SelectItem value="50-100">$50 - $100</SelectItem>
                  <SelectItem value="100+">$100+</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue placeholder="Method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                  <SelectItem value="in-person">In-Person</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8">
                    <Filter className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64">
                  <div className="grid gap-3">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Location</h4>
                      <Select>
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="local">Local Only</SelectItem>
                          <SelectItem value="remote">Remote Only</SelectItem>
                          <SelectItem value="any-location">Any Location</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Availability</h4>
                      <Select>
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select availability" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="available">Available Now</SelectItem>
                          <SelectItem value="weekdays">Weekdays</SelectItem>
                          <SelectItem value="weekends">Weekends</SelectItem>
                          <SelectItem value="any-time">Any Time</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              
              {(searchQuery || selectedCategory || priceRange || deliveryMethod) && <Button variant="ghost" size="sm" className="text-muted-foreground h-8" onClick={handleResetFilters}>
                  Clear
                </Button>}
            </div>
          </div>
        </div>

        {/* Dynamic Categories Grid - Much shorter cards with larger icons and text */}
        {isCategoriesLoading ? <div className="flex justify-center my-4">
            <p className="text-sm">Loading categories...</p>
          </div> : <div className="mb-6">
            <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-2">
              {categoriesToShow?.map(category => {
            const IconComponent = categoryIcons[category.name] || Computer;
            const colorClass = categoryColors[category.name] || "bg-gray-500 text-white";
            return <Link key={category.id} to={`/category/${category.id}`}>
                    <Card className="hover:shadow-md transition-shadow cursor-pointer dark:bg-muted/50">
                      <CardContent className="p-2 text-center h-20 flex flex-col items-center justify-center gap-1">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                          <IconComponent className="h-4 w-4 sm:h-5 sm:w-5" />
                        </div>
                        <CardTitle className="text-xs font-medium leading-tight line-clamp-2 break-words">{category.name}</CardTitle>
                      </CardContent>
                    </Card>
                  </Link>;
          })}
            </div>

            {/* Show All Button */}
            {sortedCategories && sortedCategories.length > cardsToShow && <div className="text-center mt-4">
                <Button variant="outline" size="sm" onClick={() => setShowAllCategories(!showAllCategories)}>
                  {showAllCategories ? "Show Less" : `Show All (${sortedCategories.length - cardsToShow} more)`}
                </Button>
              </div>}
          </div>}

        {/* Services Listings */}
        <div>
          <h2 className="font-poppins font-semibold text-xl mb-4">
            {selectedCategory && categories?.length > 0 ? <>Services in {getCategoryNameById(selectedCategory)}</> : 'Available Services'}
          </h2>
          
          {servicesLoading ? <div className="flex justify-center my-8">
              <p>Loading services...</p>
            </div> : filteredServices.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredServices.map(service => {
            const categoryName = service.service_categories?.name || "Uncategorized";
            const userProfile = userProfiles[service.user_id] || {
              name: "Unknown User",
              avatarInitials: "UN",
              avatarUrl: undefined
            };
            const conversationId = getConversationForService(service.id);
            const isOwnService = user && service.user_id === user.id;
            const locationName = serviceLocations[service.id];
            const IconComponent = categoryIcons[categoryName] || Computer;
            const colorClass = categoryColors[categoryName] || "bg-gray-500 text-white";
            const isExpanded = expandedCards.has(service.id);
            const description = service.description || "No description provided.";
            return <Card key={service.id} className="overflow-hidden hover:shadow-md transition-shadow relative border-2 border-green-200">
                    {/* Service Badge */}
                    <div className="absolute top-2 left-2 z-10">
                      <Badge variant="secondary" className="bg-green-500 text-white text-xs font-medium px-2 py-1">
                        SERVICE
                      </Badge>
                    </div>

                    <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700 relative overflow-hidden">
                      {service.image_url ? <>
                          <img src={service.image_url} alt={service.title} className="w-full h-full object-contain bg-gray-50 dark:bg-gray-800" />
                          <div className="absolute top-2 right-2">
                            <div className={`${colorClass} p-1.5 rounded-full shadow-lg border-2 border-white backdrop-blur-sm bg-opacity-90`}>
                              <IconComponent className="h-4 w-4" />
                            </div>
                          </div>
                        </> : <div className="w-full h-full flex items-center justify-center">
                          <div className={`${colorClass} p-2 rounded-full`}>
                            <IconComponent className="h-6 w-6" />
                          </div>
                        </div>}
                    </div>
                    
                    <CardContent className="p-4">
                      <Link to={`/profile/${service.user_id}`} className="flex items-center mb-3 hover:text-anyskill-blue">
                        <UserAvatar
                          userId={service.user_id}
                          avatarUrl={userProfile.avatarUrl}
                          fallback={userProfile.avatarInitials}
                          size="sm"
                          showLevel={true}
                          clickable={false}
                          className="mr-2"
                        />
                        <span className="text-sm text-muted-foreground hover:text-anyskill-blue truncate">{userProfile.name}</span>
                      </Link>
                      {/* Title */}
                      <CardTitle className="text-base mb-3 line-clamp-2 leading-tight dark:text-white">{service.title}</CardTitle>
                      
                      {/* Description - expandable inline */}
                      <p 
                        className={`text-sm text-muted-foreground mb-3 ${isExpanded ? '' : 'line-clamp-3'} cursor-pointer hover:text-foreground transition-colors`}
                        onClick={() => toggleExpanded(service.id)}
                        title="Click to expand/collapse"
                      >
                        {description}
                      </p>
                      
                      {/* Expand/Collapse indicator */}
                      {description.length > 150 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="p-0 h-auto text-xs text-muted-foreground hover:text-foreground mb-3"
                          onClick={() => toggleExpanded(service.id)}
                        >
                          {isExpanded ? (
                            <>Show less <ChevronUp className="h-3 w-3 ml-1" /></>
                          ) : (
                            <>Read more <ChevronDown className="h-3 w-3 ml-1" /></>
                          )}
                        </Button>
                      )}
                      
                      <ServiceLocationLabel deliveryMethod={service.delivery_method} location={locationName} className="mb-2 text-xs" />
                      
                      {service.availability_schedule && service.delivery_method !== "virtual" && (
                        <div className="mb-3">
                          <AvailabilityDisplay schedule={service.availability_schedule as WeeklySchedule} />
                        </div>
                      )}
                      
                      <div className="flex justify-between items-end">
                        <div className="flex flex-col">
                          <div className="flex items-center">
                            <span className="font-semibold text-base">${service.price}</span>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 flex-wrap">
                          {service.service_location && service.delivery_method !== "virtual" && <Button size="sm" variant="outline" onClick={() => handleViewServiceRegion(service)} className="flex items-center gap-1 text-xs px-3 py-2 h-8">
                              <Eye size={14} />
                              <span className="hidden sm:inline">View</span>
                            </Button>}
                          
                          {!isOwnService && (user ? conversationId ? <Link to={`/chat/${conversationId}`}>
                                  <Button size="sm" variant="outline" className="flex items-center gap-1 text-xs px-3 py-2 h-8">
                                    <MessageCircle size={14} />
                                    Chat
                                  </Button>
                                </Link> : <Button size="sm" onClick={() => handleRequestService(service)} disabled={requestingService === service.id} className="text-xs px-3 py-2 h-8">
                                  {requestingService === service.id ? "Requesting..." : "Request"}
                                </Button> : <Button size="sm" onClick={() => handleRequestService(service)} className="text-xs px-3 py-2 h-8">
                                Request
                              </Button>)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>;
          })}
            </div> : <div className="text-center py-12 bg-muted rounded-lg border border-border">
              <div className="text-5xl mb-4">🔍</div>
              <h3 className="font-semibold text-xl mb-2">No services found</h3>
              <p className="text-muted-foreground">
                {selectedCategory ? <>No services are available in this category at the moment.</> : <>No services match your search criteria. Try adjusting your filters.</>}
              </p>
              <Button variant="outline" className="mt-4" onClick={handleResetFilters}>
                Reset Filters
              </Button>
            </div>}
        </div>
      </div>
      
      {/* Service Region Overlay */}
      {regionOverlay.serviceLocation && (
        <Suspense fallback={null}>
          <ServiceRegionOverlay 
            isOpen={regionOverlay.isOpen} 
            onClose={handleCloseRegionOverlay} 
            serviceLocation={regionOverlay.serviceLocation} 
            serviceTitle={regionOverlay.serviceTitle || "Service"} 
          />
        </Suspense>
      )}
      
    </div>;
};
export default DiscoverServices;
